'use client';

import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ScatterChart,
  Scatter,
} from 'recharts';

interface ComparisonData {
  vendedor_id: number;
  vendedor_nome: string;
  vendedores_quantidade: number;
  vendedores_valor: number;
  vendedores_media: number;
  vendedores_desvio_padrao: number;
  tickets_quantidade: number;
  tickets_tempo_medio_horas: number;
  tickets_espera_media_minutos: number;
  tickets_transferencias_media: number;
  tickets_mensagens_media_minutos: number;
  relacao_espera_venda: number;
}

interface ComparisonChartsProps {
  comparisons: ComparisonData[];
}

export function ComparisonCharts({ comparisons }: ComparisonChartsProps) {
  if (comparisons.length === 0) {
    return <div className="text-slate-400">Selecione pelo menos um vendedor para visualizar os gráficos</div>;
  }

  const chartData = comparisons.map(comp => ({
    name: comp.vendedor_nome,
    vendas: comp.vendedores_quantidade,
    valor: Math.round(comp.vendedores_valor),
    tickets: comp.tickets_quantidade,
    tempoMedio: parseFloat(comp.tickets_tempo_medio_horas.toFixed(1)),
    esperaMedia: parseFloat(comp.tickets_espera_media_minutos.toFixed(1)),
    transferencias: parseFloat(comp.tickets_transferencias_media.toFixed(1)),
    relacao: parseFloat(comp.relacao_espera_venda.toFixed(2)),
  }));

  return (
    <div className="space-y-8">
      {/* Sales vs Tickets Comparison */}
      <div
        className="rounded-lg p-6 border"
        style={{
          backgroundColor: 'var(--card-bg)',
          borderColor: 'var(--card-border-bright)',
          backdropFilter: 'blur(10px)',
          padding: '0.5em 1.0em',
        }}
      >
        <h3 className="text-lg font-semibold text-slate-200 mb-4">Vendas vs Tickets</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.2)" />
            <XAxis dataKey="name" stroke="rgba(148, 163, 184, 0.6)" />
            <YAxis stroke="rgba(148, 163, 184, 0.6)" />
            <Tooltip
              contentStyle={{ backgroundColor: 'rgba(30, 41, 59, 0.8)', border: '1px solid rgba(148, 163, 184, 0.3)' }}
              labelStyle={{ color: '#e2e8f0' }}
            />
            <Legend />
            <Bar dataKey="vendas" fill="#3b82f6" name="Qtd Vendas" />
            <Bar dataKey="tickets" fill="#8b5cf6" name="Qtd Tickets" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Valor vs Espera Média (Efficiency) */}
      <div
        className="rounded-lg p-6 border"
        style={{
          backgroundColor: 'var(--card-bg)',
          borderColor: 'var(--card-border-bright)',
          backdropFilter: 'blur(10px)',
          padding: '0.5em 1.0em',
          marginTop: '1em',
        }}
      >
        <h3 className="text-lg font-semibold text-slate-200 mb-4">Eficiência: Valor por Hora de Espera</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.2)" />
            <XAxis dataKey="name" stroke="rgba(148, 163, 184, 0.6)" />
            <YAxis stroke="rgba(148, 163, 184, 0.6)" />
            <Tooltip
              contentStyle={{ backgroundColor: 'rgba(30, 41, 59, 0.8)', border: '1px solid rgba(148, 163, 184, 0.3)' }}
              labelStyle={{ color: '#e2e8f0' }}
              formatter={(value: any) => `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            />
            <Bar dataKey="relacao" fill="#10b981" name="R$ por Hora de Espera" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Ticket Time Analysis */}
      <div
        className="rounded-lg p-6 border"
        style={{
          backgroundColor: 'var(--card-bg)',
          borderColor: 'var(--card-border-bright)',
          backdropFilter: 'blur(10px)',
          padding: '0.5em 1.0em',
          marginTop: '1em',
        }}
      >
        <h3 className="text-lg font-semibold text-slate-200 mb-4">Análise de Tempo de Ticket</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.2)" />
            <XAxis dataKey="name" stroke="rgba(148, 163, 184, 0.6)" />
            <YAxis stroke="rgba(148, 163, 184, 0.6)" />
            <Tooltip
              contentStyle={{ backgroundColor: 'rgba(30, 41, 59, 0.8)', border: '1px solid rgba(148, 163, 184, 0.3)' }}
              labelStyle={{ color: '#e2e8f0' }}
            />
            <Legend />
            <Bar dataKey="tempoMedio" fill="#f59e0b" name="Tempo Médio (h)" />
            <Bar dataKey="esperaMedia" fill="#ef4444" name="Espera Média (min)" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Vendor Performance Scatter (Value vs Efficiency) */}
      <div
        className="rounded-lg p-6 border"
        style={{
          backgroundColor: 'var(--card-bg)',
          borderColor: 'var(--card-border-bright)',
          backdropFilter: 'blur(10px)',
          padding: '0.5em 1.0em',
          marginTop: '1em',
        }}
      >
        <h3 className="text-lg font-semibold text-slate-200 mb-4">Performance: Valor vs Eficiência</h3>
        <ResponsiveContainer width="100%" height={300}>
          <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.2)" />
            <XAxis
              dataKey="name"
              stroke="rgba(148, 163, 184, 0.6)"
              label={{ value: 'Valor Total (R$)', position: 'insideBottomRight', offset: -5 }}
            />
            <YAxis
              dataKey="relacao"
              stroke="rgba(148, 163, 184, 0.6)"
              label={{ value: 'R$ por Hora Espera', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip
              contentStyle={{ backgroundColor: 'rgba(30, 41, 59, 0.8)', border: '1px solid rgba(148, 163, 184, 0.3)' }}
              labelStyle={{ color: '#e2e8f0' }}
              cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
              content={({ active, payload }) => {
                if (active && payload && payload[0]) {
                  const data = payload[0].payload;
                  return (
                    <div className="bg-slate-800 p-2 rounded border border-slate-600">
                      <p className="text-slate-200 text-sm">{data.name}</p>
                      <p className="text-slate-300 text-xs">
                        Valor: R$ {data.valor.toLocaleString('pt-BR')}
                      </p>
                      <p className="text-slate-300 text-xs">Eficiência: R$ {data.relacao.toFixed(2)}</p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Scatter name="Vendedores" data={chartData} fill="#3b82f6" />
          </ScatterChart>
        </ResponsiveContainer>
      </div>

      {/* Transfers and Messaging Time */}
      <div
        className="rounded-lg p-6 border"
        style={{
          backgroundColor: 'var(--card-bg)',
          borderColor: 'var(--card-border-bright)',
          backdropFilter: 'blur(10px)',
          padding: '0.5em 1.0em',
          marginTop: '1em',
        }}
      >
        <h3 className="text-lg font-semibold text-slate-200 mb-4">Qualidade do Atendimento</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.2)" />
            <XAxis dataKey="name" stroke="rgba(148, 163, 184, 0.6)" />
            <YAxis stroke="rgba(148, 163, 184, 0.6)" />
            <Tooltip
              contentStyle={{ backgroundColor: 'rgba(30, 41, 59, 0.8)', border: '1px solid rgba(148, 163, 184, 0.3)' }}
              labelStyle={{ color: '#e2e8f0' }}
            />
            <Legend />
            <Bar dataKey="transferencias" fill="#ec4899" name="Transferências Médias" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
